import { Time } from "@/types/common.types.ts";
import { add, differenceInMinutes, set } from "date-fns";

export const TIME_PICKER_DEFAULT_PRESETS_FROM = "09:00";
export const TIME_PICKER_DEFAULT_PRESETS_TO = "21:00";
export const TIME_PICKER_DEFAULT_PRESETS_STEP_MINUTES = 30;
export const TIME_PICKER_DEFAULT_MAX_DURATION_MINUTES = 8 * 60;

export interface TimePickerPresetsConfig {
  from?: string;
  to?: string;
  stepMinutes?: number;
}

export interface TimePickerRangeValue {
  start: Date;
  end: Date;
}

const TIME_MASK_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const parseTimeMaskValue = (raw: string): { hours: number; minutes: number } | null => {
  const match = TIME_MASK_PATTERN.exec(raw);
  if (!match) return null;

  return { hours: parseInt(match[1], 10), minutes: parseInt(match[2], 10) };
};

export const formatTimeMaskValue = (date: Date): string => {
  return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
};

export const applyTimeToDate = (date: Date, hours: number, minutes: number): Date => {
  return set(date, { hours, minutes, seconds: 0, milliseconds: 0 });
};

export const getTimePresets = (baseDate: Date, config?: TimePickerPresetsConfig): Time[] => {
  const from = parseTimeMaskValue(config?.from ?? TIME_PICKER_DEFAULT_PRESETS_FROM);
  const to = parseTimeMaskValue(config?.to ?? TIME_PICKER_DEFAULT_PRESETS_TO);
  const stepMinutes = config?.stepMinutes ?? TIME_PICKER_DEFAULT_PRESETS_STEP_MINUTES;

  if (!from || !to || stepMinutes <= 0) return [];

  const fromMinutes = from.hours * 60 + from.minutes;
  const toMinutes = to.hours * 60 + to.minutes;

  const presets: Time[] = [];
  for (let minutes = fromMinutes; minutes <= toMinutes; minutes += stepMinutes) {
    const date = applyTimeToDate(baseDate, Math.floor(minutes / 60), minutes % 60);
    const id = formatTimeMaskValue(date);

    presets.push({ id, title: id, date });
  }

  return presets;
};

export const shiftRangeOnStartChange = (
  range: TimePickerRangeValue,
  newStart: Date
): TimePickerRangeValue => {
  const durationMinutes = differenceInMinutes(range.end, range.start);

  return { start: newStart, end: add(newStart, { minutes: durationMinutes }) };
};

export interface TimeRangeChangeResult {
  range: TimePickerRangeValue;
  error?: "end_before_start";
}

export const applyEndChange = (
  range: TimePickerRangeValue,
  newEnd: Date
): TimeRangeChangeResult => {
  if (newEnd <= range.start) {
    return { range, error: "end_before_start" };
  }

  return { range: { start: range.start, end: newEnd } };
};

export interface DurationChangeResult {
  range: TimePickerRangeValue;
  error?: "invalid" | "max_exceeded";
}

export const applyDurationChange = (
  range: TimePickerRangeValue,
  durationMinutes: number,
  maxDurationMinutes: number = TIME_PICKER_DEFAULT_MAX_DURATION_MINUTES
): DurationChangeResult => {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return { range, error: "invalid" };
  }

  if (durationMinutes > maxDurationMinutes) {
    return { range, error: "max_exceeded" };
  }

  return { range: { start: range.start, end: add(range.start, { minutes: durationMinutes }) } };
};

export const getDurationMinutes = (range: TimePickerRangeValue): number => {
  return differenceInMinutes(range.end, range.start);
};
